import { z } from 'zod';
import { ORG_ROLES, INVITABLE_ROLES } from '../roles';
import { PERMISSIONS } from '../permissions';
import { emailSchema, nameSchema, passwordSchema } from './auth';

export const orgRoleSchema = z.enum(ORG_ROLES);
export const invitableRoleSchema = z.enum(INVITABLE_ROLES);
export const permissionSchema = z.enum(PERMISSIONS);

export const userDto = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  title: z.string().nullable(),
  timezone: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});
export type UserDto = z.infer<typeof userDto>;

export const organizationDto = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  timezone: z.string(),
  currency: z.string(),
});
export type OrganizationDto = z.infer<typeof organizationDto>;

export const membershipDto = z.object({
  organization: organizationDto,
  role: orgRoleSchema,
});

export const sessionDto = z.object({
  user: userDto,
  organization: organizationDto,
  role: orgRoleSchema,
  permissions: z.array(permissionSchema),
  memberships: z.array(membershipDto),
});
export type SessionDto = z.infer<typeof sessionDto>;

export const updateOrganizationInput = z.object({
  name: z.string().trim().min(2).max(120),
  timezone: z.string().trim().min(1).max(64),
  currency: z.string().trim().length(3).toUpperCase(),
});
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationInput>;

export const memberDto = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string(),
  title: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  role: orgRoleSchema,
  status: z.enum(['active', 'disabled']),
  joinedAt: z.string(),
});
export type MemberDto = z.infer<typeof memberDto>;

export const updateMemberInput = z.object({
  role: orgRoleSchema.optional(),
  status: z.enum(['active', 'disabled']).optional(),
});
export type UpdateMemberInput = z.infer<typeof updateMemberInput>;

export const createInvitationInput = z.object({ email: emailSchema, role: invitableRoleSchema });
export type CreateInvitationInput = z.infer<typeof createInvitationInput>;

export const invitationDto = z.object({
  id: z.uuid(),
  email: z.string(),
  role: orgRoleSchema,
  invitedByName: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type InvitationDto = z.infer<typeof invitationDto>;

/** Devolvido só na criação: o link contém o token, que não é guardado em claro. */
export const createdInvitationDto = invitationDto.extend({ inviteUrl: z.string(), emailSent: z.boolean() });
export type CreatedInvitationDto = z.infer<typeof createdInvitationDto>;

export const invitationPreviewDto = z.object({
  client: z.object({ name: z.string(), color: z.string(), logoUrl: z.string().nullable() }).nullable(),
  organizationName: z.string(),
  email: z.string(),
  role: orgRoleSchema,
  invitedByName: z.string(),
  userExists: z.boolean(),
});
export type InvitationPreviewDto = z.infer<typeof invitationPreviewDto>;

export const acceptInvitationInput = z.object({ name: nameSchema.optional(), password: passwordSchema });
export type AcceptInvitationInput = z.infer<typeof acceptInvitationInput>;

export const switchOrganizationInput = z.object({ organizationId: z.uuid() });
